import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "Finance & accounting",
  title: "Cash application agent",
  cardSummary:
    "Match incoming bank receipts and POS settlements to open invoices and clear them — automatically, with only genuine exceptions going to a human.",
  heroSubhead:
    "We design, build and deploy an AI agent that reads your bank feed, POS settlements and emailed remittances, fuzzy-matches each receipt to the open invoices it pays — including part-payments and one lump sum covering several invoices — and clears the matched items in your ledger, surfacing only the receipts it genuinely can't place to your finance team.",
  snapshot: {
    valueLabel: "High",
    valueWhy:
      "Cash is applied the same day it lands instead of days later, so receivables clear faster (lower DSO), the unapplied cash sitting in a suspense account shrinks, and month-end stops being a scramble.",
    complexityLabel: "Medium–High",
    complexityWhy:
      "Fuzzy many-to-one matching across bank, POS and remittance formats, multi-currency (RM/SGD) and part-payments — and it writes the clearing entry into your ledger, so idempotency and the eval bar matter more than for a read-only flagger.",
    timeToLive: "~a few weeks (one bank account / entity, shadow mode first)",
    bestFit: "Finance teams clearing high volumes of customer receipts against open invoices every day",
  },
  outcomes: [
    "Cash recovered faster — receipts applied the day they land, so receivables clear and DSO drops",
    "Unapplied cash in the suspense account shrinks — fewer 'who paid this?' receipts sitting for days",
    "Every receipt matched consistently — including lump-sums covering several invoices and part-payments",
    "Month-end close stops being a scramble; the team works the exceptions, not the whole book",
  ],
  impact: {
    manualLabel: "Clearing a receipt by hand",
    manualValue: "~3–6 min",
    agentLabel: "With the agent",
    agentValue: "~15–30 sec review (exceptions only)",
    saved:
      "≈ 80–90% of receipts cleared without a human touch — roughly 40–70 hours a month at 1,000–1,500 receipts. And faster application means cash is recognised days sooner: shaving even 2–3 days off DSO frees working capital that dwarfs the monthly AI cost.",
    note: "Indicative; the actual match rate and time saved depend on receipt volume, how clean your payment references are, and how many payments are part-payments or lump-sums.",
  },
  businessProblem:
    "A finance team in the region downloads the day's bank statement and POS settlement file, then has to work out which customer paid, and which invoice each receipt clears. Most of it is detective work, not judgement — a statement line reads 'PYMT-4471' with no invoice number, a customer sends one round RM 4,300 that actually covers three invoices, another pays RM 500 short because of bank charges. The clerk hunts the open-invoice ledger, reasons out the split, posts the allocation in the ERP — and parks anything they can't identify in a suspense account. It's slow, it backs up, and when it backs up receipts sit unapplied, so DSO looks worse than the business actually is and month-end becomes a scramble to clear the pile.",
  manualWorkflow: {
    steps: [
      "Download the bank statement / POS settlement file and open the AR ledger",
      "Read each receipt's amount, date and payment reference",
      "Search the open invoices for the customer and the matching amount(s)",
      "Work out part-payments and lump-sums covering several invoices",
      "Post the allocation and clear the invoice(s) in the ERP",
      "Park whatever can't be identified in a suspense / unapplied account",
    ],
    breaks:
      "The slow part is the detective work — a vague reference or a round lump sum has to be reasoned back to the right open invoices — and when it backs up, receipts get parked as unapplied, so DSO looks worse than the business really is.",
  },
  agenticWorkflow: {
    narrative:
      "We build it as a routing workflow: every receipt is first classified — a clean 1:1 match, a lump-sum across several invoices, a part-payment, or an unidentifiable one — and dispatched down the right path. Clean matches run a short chain and auto-clear; anything ambiguous is verified and, if it still can't be placed confidently, routed to your finance team with the agent's best candidate matches attached.",
    steps: [
      "Read & normalise the receipt (bank feed, POS, remittance)",
      "Classify: clean match, lump-sum, part-payment or unidentified",
      "Fuzzy-match against the live open-invoice ledger",
      "Verify the allocation reconciles before clearing",
      "Clear the matched items; route the rest to a human",
    ],
    inLabel: "A receipt lands",
    outLabel: "Invoices cleared (or an exception queued)",
    loopAround: {
      index: 3,
      label:
        "Re-checks the candidate allocation — does the maths reconcile to the cent? — and re-matches before it clears anything; if confidence stays low it routes to a human instead of forcing a match.",
    },
    humanStep: "Your finance team resolves the exceptions the agent couldn't place",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "In the bank feed",
    inMessage:
      "Credit RM 4,300.00 · 14 Mar · Ref: 'SUNRISE ENT PYMT MAR'. Customer 'Sunrise Enterprise' has 3 open invoices: INV-1021 RM 1,800, INV-1044 RM 2,000, INV-1050 RM 1,200.",
    outLabel: "How the agent applies it",
    outLines: [
      "Matched to customer 'Sunrise Enterprise' from the reference and prior payment pattern",
      "RM 4,300 = INV-1021 (RM 1,800) + INV-1044 (RM 2,000) + RM 500 part-payment of INV-1050",
      "INV-1021 and INV-1044 cleared in full; INV-1050 part-paid, RM 700 still open",
      "Nothing left unapplied — the full RM 4,300 is allocated",
    ],
    outResult: "3 invoices updated, 2 cleared · auto-posted to the ledger · RM 0 left unapplied",
    note: "Every allocation traces to the receipt and invoice IDs · reversible in one step if a human overrides · ~RM 0.05–0.15 per receipt in AI cost.",
  },
  workflowDetail: [
    {
      title: "Read & normalise the receipt",
      detail:
        "The agent ingests the day's receipts from wherever they land — a bank feed (CAMT/MT940), a CSV, a POS settlement file, or an emailed remittance advice — and normalises each into a common shape: amount, currency, value date, payer and whatever reference text came with it, reading PDFs and images with vision where needed.",
    },
    {
      title: "Classify the receipt",
      detail:
        "It routes each receipt by type — a clean single-invoice match, one lump sum covering several invoices, a part-payment, or one it can't yet identify — because each needs different handling. This is the routing step that keeps the common case fast and the hard cases careful.",
    },
    {
      title: "Fuzzy-match against the open ledger",
      detail:
        "Using the customer's live open invoices and their prior payment pattern, it matches the receipt to the invoice(s) it pays — reasoning past a cryptic reference, a name that doesn't quite match, or a round sum that has to be split across several open items.",
    },
    {
      title: "Verify and re-check (the loop)",
      detail:
        "Before clearing anything it re-checks that the allocation reconciles exactly — the picked invoices sum to the receipt, the currency and open status are right — and re-matches if not. Where it still can't reconcile confidently, it stops rather than force a match.",
    },
    {
      title: "Clear the matched items",
      detail:
        "For confident, reconciled matches it posts the allocation to your ledger and clears the invoice(s) — with an idempotency key so a replayed statement can't double-apply, and as a reversible entry so your team can override it in one step.",
    },
    {
      title: "Hand exceptions to a human",
      detail:
        "Whatever it can't place — an unknown payer, a mismatch, a reference that leads nowhere — is routed to your finance team with the candidates it considered, so the detective work is already done. Nothing is cleared on a guess.",
    },
  ],
  howWeBuild:
    "It works from your live open-invoice ledger, so it matches against what is actually outstanding right now — an invoice already paid or credited can't be matched twice. When it clears an item it writes a structured allocation through a connected tool carrying an idempotency key, so a re-run or a duplicated bank line can't double-apply a receipt, and every allocation is a reversible journal your team can undo in one step. It never moves money — it only allocates receipts you have already banked against the invoices they pay, and it starts in shadow mode, proposing matches for a human to confirm until its accuracy on your real receipts has earned the auto-clear.",
  agentLoop: [
    "the receipt + your live open-invoice ledger & this payer's history",
    "classify, fuzzy-match & allocate (MCP tools)",
    "re-check the allocation reconciles to the cent before it clears",
  ],
  integrations: [
    "Bank feed / statement import (CAMT / MT940, CSV or PDF remittance)",
    "POS settlement / payment-gateway payout files",
    "ERP / accounting ledger (AR open items, cash posting)",
  ],
  architecture: [
    "Pattern: a routing front-end on the Claude Agent SDK dispatches each receipt (clean 1:1 · lump-sum · part-payment · unidentified) into a short per-type chain; each tool is exposed via an in-process MCP server.",
    "Context: the customer's live open-invoice ledger and their recent payment history are loaded per receipt, so matching is against what is outstanding now and informed by how this payer usually pays — their references, rounding, and which entity they settle to.",
    "Tools: read-only open-AR and payment-history lookups, a normaliser for bank/POS/remittance formats, a deterministic allocation solver (which open invoices sum to this receipt), and one write tool that posts an allocation to the ledger — no tool can move money or create, edit or delete an invoice.",
    "Idempotency & reversibility: every posting carries an idempotency key (bank-line ID + amount + value date) so a replayed statement or duplicated feed can't double-apply, and each allocation is a single-step reversible journal, never a silent overwrite.",
    "Guardrails & gate: auto-clear fires only above a confidence threshold and only when the allocation reconciles to the cent; part-payments, over-payments, FX rounding beyond tolerance and anything below threshold route to a human queue with candidate matches attached — never a forced match.",
    "Untrusted input & PDPA/PDPC: statement narratives and remittance emails are treated as untrusted text (a crafted reference can't steer a match), and bank details, customer names and amounts are handled PDPA/PDPC-aligned across the MY and SG entities.",
    "Multi-currency: RM and SGD receipts are matched in each invoice's currency using the booked FX, with a tolerance for bank charges and rounding, so a receipt short by a few cents of fees still clears cleanly.",
    "Observability & audit: every match decision, the candidates considered, and each posting or reversal are logged and traceable to the bank-line and invoice IDs — a machine-readable audit trail suited to Finance controls and an external audit.",
    "Eval harness: a golden set of real historical receipts — clean, lump-sum, part-paid, mis-referenced and unidentifiable — with deterministic reconciliation checks plus an LLM-as-judge on the fuzzy calls, regression-tested before any change touches live posting.",
  ],
  models: [
    {
      task: "Classify & fuzzy-match receipts to invoices",
      model: "Claude Sonnet",
      why: "Reads messy references and reasons out lump-sums and part-payments — the core judgement, at moderate cost.",
    },
    {
      task: "High-volume clean 1:1 matches",
      model: "Claude Haiku",
      why: "Most receipts are a clean single-invoice match; cheapest and fastest for the bulk of the volume.",
    },
    {
      task: "Normalise varied statement / remittance formats",
      model: "Claude Haiku",
      why: "Structured extraction from CSV / MT940 / PDF lines — a light, high-frequency task.",
    },
    {
      task: "Resolve tangled or disputed allocations",
      model: "Claude Opus",
      why: "Invoked only for the hardest lump-sums and disputes Sonnet flags — a small fraction of receipts.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per receipt",
        value: "~RM 0.05–0.15",
        note: "normalise + match + verify + posting, mostly at Claude Haiku/Sonnet token rates",
      },
      {
        label: "AI usage — at 1,500 receipts/month",
        value: "~RM 65–200 / month",
        note: "lower again with the Batch API for end-of-day, non-urgent runs",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "connecting your bank feed, POS payouts and ERP posting is a few weeks; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to the working capital freed by even a couple of days lower DSO",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on receipt volume, how many are part-payments or lump-sums, and how clean your references are. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  modelChoice: {
    approach: "Single model, tiered by task",
    rationale:
      "Applying a receipt shares one context — the receipt and the customer's open items — so a single well-instrumented model runs the judgement, with cheaper Haiku on the many clean matches and Opus reserved only for the tangled ones. Reliable, cheap per receipt, and easy to audit.",
    costNote:
      "We reserve multi-agent designs (≈15× the tokens) for genuinely parallel, breadth-first work — not for a single receipt-to-invoice reconciliation.",
  },
  evaluation: [
    "Deterministic checks first — does the allocation sum exactly to the receipt, is the invoice actually open, is the currency right",
    "Self-verification — the agent re-checks the maths and re-matches before it clears, and queues rather than force a match",
    "Business KPIs — auto-match rate, cash-application time, unapplied-cash balance, DSO",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we measure your current cash application on a sample — time per receipt, what share you match automatically today, how much sits unapplied at month-end, and how often two clerks allocate a tricky receipt the same way. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it apply the cash correctly?",
        metrics: [
          {
            name: "Allocation vs a senior AR clerk",
            measures:
              "Its cleared/queued decision and the invoices it picks agree with an expert's on held-out receipts",
            target: "≥ 98%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Was the matching sound and complete?",
        metrics: [
          {
            name: "Required checks executed",
            measures:
              "Every candidate check is run — customer, amount, reference, open status, currency — nothing skipped",
            target: "100%",
          },
          {
            name: "No forced matches",
            measures: "It queues rather than guess when candidates don't reconcile to the receipt",
            target: "monitored",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual steps correct?",
        metrics: [
          {
            name: "Allocation accuracy",
            measures: "The invoices and amounts it clears are exactly right, to the cent",
            target: "≥ 99%",
          },
          {
            name: "Auto-match rate",
            measures: "Share of receipts cleared without a human, at the accuracy bar",
            target: "≥ 80% (target)",
          },
          {
            name: "Mis-application rate",
            measures: "How often it clears the wrong invoice",
            target: "0 tolerated",
          },
          {
            name: "No unapplied leakage",
            measures: "Every cleared receipt is fully allocated to the cent — no stray balance",
            target: "100%",
          },
        ],
      },
    ],
    gate: "The agent does not post to a live ledger until it beats the human baseline and clears these GPA targets on a golden set of historical receipts — clean, lump-sum, part-paid and mis-referenced. Auto-clear starts in shadow mode (it proposes, a human confirms) and only goes live per receipt-type once its accuracy holds. Every change is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on your real historical bank statements and open-invoice ledger — including the lump-sums and mis-referenced payments — running in shadow mode so you can see its matches before it clears anything. A focused single-account rollout is a matter of weeks.",
  ctaTitle: "Is your team clearing bank receipts by hand?",
  ctaDescription:
    "Tell us your receipt volume, banks and ERP, and we'll tell you honestly whether a cash application agent is worth building — and what auto-match rate to expect.",
  faqs: [
    {
      question: "Does the agent move or touch our money?",
      answer:
        "No. It never initiates a payment or transfer. It only allocates receipts you have already banked against the open invoices they pay, and posts that allocation in your ledger — every posting is reversible in one step, and money movement is never in scope.",
    },
    {
      question: "How does it handle one payment covering several invoices, or a part-payment?",
      answer:
        "That's the core of it. It reasons a lump sum back to the invoices that sum to it, splits a part-payment against the right invoice and leaves the balance open, and where the maths doesn't reconcile it queues the receipt with its best candidates rather than forcing a match.",
    },
    {
      question: "What about receipts it can't identify?",
      answer:
        "They don't get guessed. Anything below the confidence bar — a cryptic reference, a mismatch, an unknown payer — is routed to your finance team with the candidates the agent considered: the same ones that would have sat in unapplied cash anyway, but with the detective work already done.",
    },
    {
      question: "Does it work across Malaysia and Singapore and multiple currencies?",
      answer:
        "Yes. It matches RM and SGD receipts in each invoice's currency using the booked FX with a tolerance for bank charges, and it handles both entities' ledgers. Treat tax and audit specifics as a starting point for your finance team, not accounting advice.",
    },
    {
      question: "How long does it take to deploy?",
      answer:
        "A proof-of-concept on one bank account or entity is usually a matter of weeks, running in shadow mode first; a full rollout takes longer — mostly ERP posting integration and your team getting comfortable letting it auto-clear, not the AI.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "Kewangan & perakaunan",
  title: "Ejen aplikasi tunai",
  cardSummary:
    "Padankan resit bank dan penyelesaian POS masuk dengan invois belum jelas dan selesaikannya — secara automatik, dengan hanya pengecualian sebenar diserahkan kepada manusia.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI yang membaca suapan bank, penyelesaian POS dan resit dihantar e-mel anda, memadankan setiap resit secara kabur dengan invois belum jelas yang dibayarnya — termasuk bayaran separa dan satu bayaran pukal meliputi beberapa invois — dan menjelaskan item yang dipadankan dalam lejar anda, hanya memaparkan resit yang benar-benar tidak dapat ditempatkan kepada pasukan kewangan anda.",
  snapshot: {
    valueLabel: "Tinggi",
    valueWhy:
      "Tunai diaplikasikan pada hari yang sama ia masuk, bukan beberapa hari kemudian, jadi belum terima jelas lebih cepat (DSO lebih rendah), tunai belum diaplikasi dalam akaun suspens mengecil, dan hujung bulan tidak lagi kelam-kabut.",
    complexityLabel: "Sederhana–Tinggi",
    complexityWhy:
      "Padanan kabur banyak-ke-satu merentas format bank, POS dan resit, pelbagai mata wang (RM/SGD) dan bayaran separa — dan ia menulis entri penjelasan ke lejar anda, jadi idempotensi dan tahap penilaian lebih penting berbanding penanda baca-sahaja.",
    timeToLive: "~beberapa minggu (satu akaun bank / entiti, mod bayang dahulu)",
    bestFit: "Pasukan kewangan yang menjelaskan volum tinggi resit pelanggan terhadap invois belum jelas setiap hari",
  },
  outcomes: [
    "Tunai dipulihkan lebih cepat — resit diaplikasi pada hari ia masuk, jadi belum terima jelas dan DSO turun",
    "Tunai belum diaplikasi dalam akaun suspens mengecil — kurang resit 'siapa bayar ini?' terbengkalai berhari-hari",
    "Setiap resit dipadankan secara konsisten — termasuk bayaran pukal meliputi beberapa invois dan bayaran separa",
    "Penutupan hujung bulan tidak lagi kelam-kabut; pasukan menguruskan pengecualian, bukan seluruh buku",
  ],
  impact: {
    manualLabel: "Jelaskan resit secara manual",
    manualValue: "~3–6 min",
    agentLabel: "Dengan ejen",
    agentValue: "~15–30 saat semak (pengecualian sahaja)",
    saved:
      "≈ 80–90% resit dijelaskan tanpa sentuhan manusia — kira-kira 40–70 jam sebulan pada 1,000–1,500 resit. Dan aplikasi lebih cepat bermakna tunai diiktiraf beberapa hari lebih awal: mengurangkan DSO walau 2–3 hari membebaskan modal kerja yang mengatasi kos AI bulanan.",
    note: "Anggaran; kadar padanan dan masa dijimatkan sebenar bergantung pada volum resit, sejauh mana bersih rujukan bayaran anda, dan berapa banyak bayaran ialah separa atau pukal.",
  },
  businessProblem:
    "Pasukan kewangan sebuah syarikat di rantau ini memuat turun penyata bank dan fail penyelesaian POS harian, kemudian perlu mengetahui pelanggan mana yang membayar, dan invois mana setiap resit menjelaskan. Kebanyakannya kerja siasat, bukan pertimbangan — satu baris penyata tertulis 'PYMT-4471' tanpa nombor invois, seorang pelanggan menghantar satu RM 4,300 bulat yang sebenarnya meliputi tiga invois, seorang lagi membayar RM 500 kurang kerana caj bank. Kerani memburu lejar invois belum jelas, menaakul pecahan, mencatat peruntukan dalam ERP — dan meletakkan apa-apa yang tidak dapat dikenal pasti dalam akaun suspens. Ia perlahan, tersekat, dan apabila tersekat resit terbengkalai belum diaplikasi, jadi DSO kelihatan lebih teruk daripada keadaan perniagaan sebenar dan hujung bulan menjadi kelam-kabut untuk menjelaskan longgokan.",
  manualWorkflow: {
    steps: [
      "Muat turun penyata bank / fail penyelesaian POS dan buka lejar AR",
      "Baca jumlah, tarikh dan rujukan bayaran setiap resit",
      "Cari invois belum jelas untuk pelanggan dan jumlah yang sepadan",
      "Selesaikan bayaran separa dan bayaran pukal meliputi beberapa invois",
      "Catat peruntukan dan jelaskan invois dalam ERP",
      "Letakkan apa-apa yang tidak dapat dikenal pasti dalam akaun suspens / belum diaplikasi",
    ],
    breaks:
      "Bahagian perlahan ialah kerja siasat — rujukan samar atau jumlah pukal bulat perlu ditaakul kembali ke invois belum jelas yang betul — dan apabila tersekat, resit terbengkalai belum diaplikasi, jadi DSO kelihatan lebih teruk daripada keadaan sebenar perniagaan.",
  },
  agenticWorkflow: {
    narrative:
      "Kami membinanya sebagai aliran routing: setiap resit mula-mula dikelaskan — padanan 1:1 bersih, bayaran pukal merentas beberapa invois, bayaran separa, atau satu yang tidak dapat dikenal pasti — dan dihantar ke laluan yang betul. Padanan bersih menjalankan rantaian pendek dan menjelaskan secara automatik; apa-apa yang samar disahkan dan, jika masih tidak dapat ditempatkan dengan yakin, dihalakan kepada pasukan kewangan anda dengan padanan calon terbaik ejen dilampirkan.",
    steps: [
      "Baca & normalkan resit (suapan bank, POS, resit)",
      "Kelaskan: padanan bersih, bayaran pukal, bayaran separa atau tidak dikenali",
      "Padan kabur terhadap lejar invois belum jelas langsung",
      "Sahkan peruntukan sepadan sebelum menjelaskan",
      "Jelaskan item yang dipadankan; halakan selebihnya kepada manusia",
    ],
    inLabel: "Resit masuk",
    outLabel: "Invois dijelaskan (atau pengecualian dibaris-gilirkan)",
    loopAround: {
      index: 3,
      label:
        "Menyemak semula peruntukan calon — adakah kiraan sepadan hingga sen? — dan memadan semula sebelum menjelaskan apa-apa; jika keyakinan kekal rendah ia menghalakan kepada manusia dan bukan memaksa padanan.",
    },
    humanStep: "Pasukan kewangan anda menyelesaikan pengecualian yang ejen tidak dapat tempatkan",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "Dalam suapan bank",
    inMessage:
      "Kredit RM 4,300.00 · 14 Mac · Ruj: 'SUNRISE ENT PYMT MAR'. Pelanggan 'Sunrise Enterprise' ada 3 invois belum jelas: INV-1021 RM 1,800, INV-1044 RM 2,000, INV-1050 RM 1,200.",
    outLabel: "Cara ejen mengaplikasinya",
    outLines: [
      "Dipadankan dengan pelanggan 'Sunrise Enterprise' daripada rujukan dan corak bayaran terdahulu",
      "RM 4,300 = INV-1021 (RM 1,800) + INV-1044 (RM 2,000) + RM 500 bayaran separa INV-1050",
      "INV-1021 dan INV-1044 dijelaskan penuh; INV-1050 dibayar separa, RM 700 masih belum jelas",
      "Tiada baki belum diaplikasi — RM 4,300 penuh diperuntukkan",
    ],
    outResult: "3 invois dikemas kini, 2 dijelaskan · dicatat automatik ke lejar · RM 0 belum diaplikasi",
    note: "Setiap peruntukan dijejak ke ID resit dan invois · boleh diterbalikkan satu langkah jika manusia menolak · ~RM 0.05–0.15 setiap resit dalam kos AI.",
  },
  workflowDetail: [
    {
      title: "Baca & normalkan resit",
      detail:
        "Ejen membaca resit harian dari mana-mana ia masuk — suapan bank (CAMT/MT940), CSV, fail penyelesaian POS, atau nasihat resit e-mel — dan menormalkan setiap satu ke bentuk sepunya: jumlah, mata wang, tarikh nilai, pembayar dan apa-apa teks rujukan yang datang bersamanya, membaca PDF dan imej dengan visi apabila perlu.",
    },
    {
      title: "Kelaskan resit",
      detail:
        "Ia menghalakan setiap resit mengikut jenis — padanan invois tunggal bersih, satu bayaran pukal meliputi beberapa invois, bayaran separa, atau satu yang belum dapat dikenal pasti — kerana setiap satu memerlukan pengendalian berbeza. Ini langkah routing yang memastikan kes biasa pantas dan kes sukar berhati-hati.",
    },
    {
      title: "Padan kabur terhadap lejar belum jelas",
      detail:
        "Menggunakan invois belum jelas langsung pelanggan dan corak bayaran terdahulu mereka, ia memadankan resit dengan invois yang dibayarnya — menaakul melepasi rujukan samar, nama yang tidak betul-betul sepadan, atau jumlah bulat yang perlu dipecahkan merentas beberapa item belum jelas.",
    },
    {
      title: "Sahkan dan semak semula (gelung)",
      detail:
        "Sebelum menjelaskan apa-apa ia menyemak semula bahawa peruntukan sepadan tepat — invois yang dipilih berjumlah sama dengan resit, mata wang dan status belum jelas betul — dan memadan semula jika tidak. Apabila ia masih tidak dapat sepadan dengan yakin, ia berhenti dan bukan memaksa padanan.",
    },
    {
      title: "Jelaskan item yang dipadankan",
      detail:
        "Untuk padanan yakin dan sepadan, ia mencatat peruntukan ke lejar anda dan menjelaskan invois — dengan kunci idempotensi supaya penyata dimainkan semula tidak boleh mengaplikasi dua kali, dan sebagai entri boleh terbalik supaya pasukan anda boleh menolaknya satu langkah.",
    },
    {
      title: "Serah pengecualian kepada manusia",
      detail:
        "Apa-apa yang tidak dapat ditempatkan — pembayar tidak dikenali, ketidakpadanan, rujukan yang tidak membawa ke mana-mana — dihalakan kepada pasukan kewangan anda dengan calon yang dipertimbangkannya, jadi kerja siasat sudah selesai. Tiada apa dijelaskan atas tekaan.",
    },
  ],
  howWeBuild:
    "Ia bekerja daripada lejar invois belum jelas langsung anda, jadi ia memadan terhadap apa yang benar-benar tertunggak sekarang — invois yang sudah dibayar atau dikreditkan tidak boleh dipadankan dua kali. Apabila ia menjelaskan item, ia menulis peruntukan berstruktur melalui alat yang disambung membawa kunci idempotensi, jadi larian semula atau baris bank berganda tidak boleh mengaplikasi resit dua kali, dan setiap peruntukan ialah entri lejar boleh terbalik yang pasukan anda boleh batalkan satu langkah. Ia tidak pernah memindahkan wang — ia hanya memperuntukkan resit yang telah anda masukkan ke bank terhadap invois yang dibayarnya, dan ia bermula dalam mod bayang, mencadangkan padanan untuk manusia sahkan sehingga ketepatannya pada resit sebenar anda telah mendapat auto-jelas.",
  agentLoop: [
    "resit + lejar invois belum jelas langsung anda & sejarah pembayar ini",
    "kelas, padan kabur & peruntuk (alat MCP)",
    "semak semula peruntukan sepadan hingga sen sebelum ia menjelaskan",
  ],
  integrations: [
    "Suapan bank / import penyata (CAMT / MT940, CSV atau resit PDF)",
    "Fail penyelesaian POS / bayaran gerbang pembayaran",
    "Lejar ERP / perakaunan (item belum jelas AR, catatan tunai)",
  ],
  architecture: [
    "Corak: bahagian hadapan routing atas Claude Agent SDK menghantar setiap resit (1:1 bersih · pukal · bayaran separa · tidak dikenali) ke rantaian pendek per-jenis; setiap alat didedahkan melalui pelayan MCP dalam-proses.",
    "Konteks: lejar invois belum jelas langsung pelanggan dan sejarah bayaran terkini mereka dimuatkan per resit, jadi padanan adalah terhadap apa yang tertunggak sekarang dan dimaklumkan oleh cara pembayar ini biasanya membayar — rujukan, pembundaran, dan entiti mana mereka jelaskan.",
    "Alat: semakan AR-belum-jelas dan sejarah-bayaran baca-sahaja, penormal untuk format bank/POS/resit, penyelesai peruntukan deterministik (invois belum jelas mana berjumlah resit ini), dan satu alat tulis yang mencatat peruntukan ke lejar — tiada alat boleh memindahkan wang atau mencipta, mengedit atau memadam invois.",
    "Idempotensi & keterbalikan: setiap catatan membawa kunci idempotensi (ID baris bank + jumlah + tarikh nilai) supaya penyata dimainkan semula atau suapan berganda tidak boleh mengaplikasi dua kali, dan setiap peruntukan ialah entri lejar boleh terbalik satu langkah, bukan tulis-ganti senyap.",
    "Pagar keselamatan & get: auto-jelas hanya berlaku melebihi ambang keyakinan dan hanya apabila peruntukan sepadan hingga sen; bayaran separa, bayaran lebih, pembundaran FX melebihi toleransi dan apa-apa di bawah ambang dihalakan ke baris gilir manusia dengan padanan calon dilampirkan — tidak pernah padanan paksa.",
    "Input tidak dipercayai & PDPA/PDPC: naratif penyata dan e-mel resit dianggap teks tidak dipercayai (rujukan yang direka tidak boleh mengemudi padanan), dan butiran bank, nama pelanggan dan jumlah dikendalikan selaras PDPA/PDPC merentas entiti MY dan SG.",
    "Pelbagai mata wang: resit RM dan SGD dipadankan dalam mata wang setiap invois menggunakan FX ditempah, dengan toleransi untuk caj bank dan pembundaran, jadi resit yang kurang beberapa sen kerana yuran tetap dijelaskan dengan bersih.",
    "Kebolehlihatan & audit: setiap keputusan padanan, calon yang dipertimbangkan, dan setiap catatan atau pembalikan dilog dan boleh dijejak ke ID baris bank dan invois — jejak audit boleh-dibaca-mesin sesuai untuk kawalan Kewangan dan audit luar.",
    "Rangka penilaian: set emas resit sejarah sebenar — bersih, pukal, dibayar separa, salah rujuk dan tidak dikenali — dengan semakan rekonsiliasi deterministik serta LLM-sebagai-hakim untuk kes kabur, diuji regresi sebelum apa-apa perubahan menyentuh catatan langsung.",
  ],
  models: [
    {
      task: "Kelas & padan kabur resit ke invois",
      model: "Claude Sonnet",
      why: "Membaca rujukan berselerak dan menaakul bayaran pukal serta separa — pertimbangan teras, pada kos sederhana.",
    },
    {
      task: "Padanan 1:1 bersih bervolum tinggi",
      model: "Claude Haiku",
      why: "Kebanyakan resit ialah padanan invois tunggal bersih; paling murah dan pantas untuk sebahagian besar volum.",
    },
    {
      task: "Normalkan format penyata / resit yang pelbagai",
      model: "Claude Haiku",
      why: "Ekstrak berstruktur daripada baris CSV / MT940 / PDF — tugas ringan berfrekuensi tinggi.",
    },
    {
      task: "Selesaikan peruntukan berbelit atau dipertikai",
      model: "Claude Opus",
      why: "Digunakan hanya untuk bayaran pukal paling sukar dan pertikaian yang Sonnet tandakan — sebahagian kecil resit.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap resit",
        value: "~RM 0.05–0.15",
        note: "normal + padan + sahkan + catat, kebanyakannya pada kadar token Claude Haiku/Sonnet",
      },
      {
        label: "Penggunaan AI — pada 1,500 resit/bulan",
        value: "~RM 65–200 / bulan",
        note: "lebih rendah lagi dengan Batch API untuk larian bukan segera hujung hari",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "menyambung suapan bank, bayaran POS dan catatan ERP anda adalah beberapa minggu; kami beri sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding modal kerja yang dibebaskan oleh DSO yang lebih rendah walau beberapa hari",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada volum resit, berapa banyak ialah bayaran separa atau pukal, dan sejauh mana bersih rujukan anda. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  modelChoice: {
    approach: "Model tunggal, berperingkat mengikut tugas",
    rationale:
      "Mengaplikasi resit berkongsi satu konteks — resit dan item belum jelas pelanggan — jadi model tunggal yang lengkap menjalankan pertimbangan, dengan Haiku lebih murah pada banyak padanan bersih dan Opus disimpan hanya untuk yang berbelit. Boleh dipercayai, murah setiap resit, dan mudah diaudit.",
    costNote:
      "Kami menyimpan reka bentuk berbilang-ejen (≈15× token) untuk kerja yang benar-benar selari — bukan untuk satu rekonsiliasi resit-ke-invois.",
  },
  evaluation: [
    "Semakan deterministik dahulu — adakah peruntukan berjumlah tepat dengan resit, adakah invois benar-benar belum jelas, adakah mata wang betul",
    "Pengesahan sendiri — ejen menyemak semula kiraan dan memadan semula sebelum menjelaskan, dan membaris-gilir dan bukan memaksa padanan",
    "KPI perniagaan — kadar auto-padan, masa aplikasi tunai, baki tunai belum diaplikasi, DSO",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mengukur aplikasi tunai semasa anda pada sampel — masa setiap resit, berapa peratus anda padankan secara automatik hari ini, berapa banyak terbengkalai belum diaplikasi pada hujung bulan, dan berapa kerap dua kerani memperuntukkan resit sukar dengan cara sama. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan, bukan diandaikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia mengaplikasi tunai dengan betul?",
        metrics: [
          {
            name: "Peruntukan vs kerani AR kanan",
            measures:
              "Keputusan jelas/baris-gilirnya dan invois yang dipilih bersetuju dengan pakar pada resit terpencil",
            target: "≥ 98%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah padanan kukuh dan lengkap?",
        metrics: [
          {
            name: "Semakan diperlukan dilaksanakan",
            measures:
              "Setiap semakan calon dijalankan — pelanggan, jumlah, rujukan, status belum jelas, mata wang — tiada dilangkau",
            target: "100%",
          },
          {
            name: "Tiada padanan paksa",
            measures: "Ia membaris-gilir dan bukan meneka apabila calon tidak sepadan dengan resit",
            target: "dipantau",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap langkah betul?",
        metrics: [
          {
            name: "Ketepatan peruntukan",
            measures: "Invois dan jumlah yang dijelaskan tepat betul, hingga sen",
            target: "≥ 99%",
          },
          {
            name: "Kadar auto-padan",
            measures: "Peratusan resit dijelaskan tanpa manusia, pada tahap ketepatan",
            target: "≥ 80% (sasaran)",
          },
          {
            name: "Kadar salah-aplikasi",
            measures: "Berapa kerap ia menjelaskan invois yang salah",
            target: "0 dibenarkan",
          },
          {
            name: "Tiada kebocoran belum diaplikasi",
            measures: "Setiap resit dijelaskan diperuntukkan penuh hingga sen — tiada baki tersasar",
            target: "100%",
          },
        ],
      },
    ],
    gate: "Ejen tidak mencatat ke lejar langsung sehingga ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas resit sejarah — bersih, pukal, dibayar separa dan salah rujuk. Auto-jelas bermula dalam mod bayang (ia mencadang, manusia sahkan) dan hanya beroperasi langsung per jenis-resit apabila ketepatannya kekal. Setiap perubahan diuji regresi terhadap set yang sama sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas penyata bank dan lejar invois belum jelas sejarah sebenar anda — termasuk bayaran pukal dan salah rujuk — berjalan dalam mod bayang supaya anda boleh melihat padanannya sebelum ia menjelaskan apa-apa. Pelancaran satu akaun yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Adakah pasukan anda menjelaskan resit bank secara manual?",
  ctaDescription:
    "Beritahu kami volum resit, bank dan ERP anda, dan kami akan beritahu dengan jujur sama ada ejen aplikasi tunai berbaloi dibina — dan kadar auto-padan yang boleh dijangka.",
  faqs: [
    {
      question: "Adakah ejen memindahkan atau menyentuh wang kami?",
      answer:
        "Tidak. Ia tidak pernah memulakan bayaran atau pindahan. Ia hanya memperuntukkan resit yang telah anda masukkan ke bank terhadap invois belum jelas yang dibayarnya, dan mencatat peruntukan itu dalam lejar anda — setiap catatan boleh diterbalikkan satu langkah, dan pergerakan wang tidak pernah dalam skop.",
    },
    {
      question: "Bagaimana ia mengendalikan satu bayaran meliputi beberapa invois, atau bayaran separa?",
      answer:
        "Itulah terasnya. Ia menaakul jumlah pukal kembali ke invois yang berjumlah dengannya, memecah bayaran separa terhadap invois yang betul dan membiarkan baki belum jelas, dan apabila kiraan tidak sepadan ia membaris-gilir resit dengan calon terbaiknya dan bukan memaksa padanan.",
    },
    {
      question: "Bagaimana dengan resit yang ia tidak dapat kenal pasti?",
      answer:
        "Ia tidak diteka. Apa-apa di bawah tahap keyakinan — rujukan samar, ketidakpadanan, pembayar tidak dikenali — dihalakan kepada pasukan kewangan anda dengan calon yang dipertimbangkan ejen: yang sama akan terbengkalai sebagai tunai belum diaplikasi, tetapi dengan kerja siasat sudah selesai.",
    },
    {
      question: "Adakah ia berfungsi merentas Malaysia dan Singapura dan pelbagai mata wang?",
      answer:
        "Ya. Ia memadankan resit RM dan SGD dalam mata wang setiap invois menggunakan FX ditempah dengan toleransi untuk caj bank, dan ia mengendalikan lejar kedua-dua entiti. Anggap spesifik cukai dan audit sebagai titik permulaan untuk pasukan kewangan anda, bukan nasihat perakaunan.",
    },
    {
      question: "Berapa lama untuk dilaksanakan?",
      answer:
        "Bukti-konsep atas satu akaun bank atau entiti biasanya beberapa minggu, berjalan dalam mod bayang dahulu; pelancaran penuh mengambil masa lebih lama — kebanyakannya integrasi catatan ERP dan membiasakan pasukan anda membenarkan ia auto-jelas, bukan AI.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "财务与会计",
  title: "现金核销智能体",
  cardSummary:
    "把入账的银行收款和 POS 结算匹配到未清发票并核销——自动完成，只有真正的例外才交给人处理。",
  heroSubhead:
    "我们设计、构建并部署一个 AI 智能体，读取你的银行流水、POS 结算和邮件汇款通知，将每一笔收款模糊匹配到它所支付的未清发票——包括部分付款和一笔覆盖多张发票的整付款——并在你的账簿中核销已匹配的项目，只把它确实无法归位的收款呈交给财务团队。",
  snapshot: {
    valueLabel: "高",
    valueWhy:
      "现金在到账当天即完成核销，而非数天之后，因此应收更快清账（DSO 更低），暂挂账户中的未核销现金减少，月末也不再手忙脚乱。",
    complexityLabel: "中等–偏高",
    complexityWhy:
      "跨银行、POS 与汇款格式的多对一模糊匹配、多币种（RM/SGD）与部分付款——而且它会把核销分录写入你的账簿，因此幂等性与评估门槛比只读标记器更为关键。",
    timeToLive: "约数周（单一银行账户 / 实体，先影子模式）",
    bestFit: "每天要将大量客户收款核销到未清发票的财务团队",
  },
  outcomes: [
    "现金回收更快——收款在到账当天入账，应收得以清账、DSO 下降",
    "暂挂账户中的未核销现金减少——更少「这是谁付的？」的收款搁置数天",
    "每一笔收款都被一致地匹配——包括覆盖多张发票的整付款与部分付款",
    "月末结账不再手忙脚乱；团队处理例外，而非整本账",
  ],
  impact: {
    manualLabel: "人工核销一笔收款",
    manualValue: "约 3–6 分钟",
    agentLabel: "使用智能体",
    agentValue: "约 15–30 秒复核（仅例外）",
    saved:
      "约 80–90% 的收款无需人工即可核销——每月 1,000–1,500 笔约合 40–70 小时。而更快的入账意味着现金提前数天确认：DSO 哪怕缩短 2–3 天，所释放的营运资金也远超每月 AI 成本。",
    note: "为估算；实际匹配率与节省的时间取决于收款笔数、你的付款摘要有多规范，以及有多少是部分付款或整付款。",
  },
  businessProblem:
    "区域内一家公司的财务团队每天下载银行对账单和 POS 结算文件，随后要弄清是哪位客户付了款、每笔收款核销哪张发票。这大多是侦探活，而非判断——一行流水写着「PYMT-4471」却没有发票号，一位客户汇来一笔整数 RM 4,300，实际覆盖三张发票，另一位因银行手续费少付了 RM 500。文员翻查未清发票账、推断拆分、在 ERP 中登记核销——把无法识别的都挂进暂挂账户。这个过程缓慢、容易堆积，一旦堆积，收款便搁置为未核销，于是 DSO 看起来比业务实际更差，月末则变成清理这堆积压的忙乱。",
  manualWorkflow: {
    steps: [
      "下载银行对账单 / POS 结算文件并打开应收账簿",
      "读取每笔收款的金额、日期与付款摘要",
      "在未清发票中查找对应客户与匹配金额",
      "厘清部分付款与覆盖多张发票的整付款",
      "在 ERP 中登记核销并清账",
      "把无法识别的挂入暂挂 / 未核销账户",
    ],
    breaks:
      "缓慢之处在于侦探活——一个含糊的摘要或一笔整数款要被推断回正确的未清发票——一旦堆积，收款便搁置为未核销，于是 DSO 看起来比业务实际更差。",
  },
  agenticWorkflow: {
    narrative:
      "我们以 routing（路由）方式构建：每一笔收款先被分类——干净的 1:1 匹配、覆盖多张发票的整付款、部分付款，或无法识别的一笔——再分派到相应路径。干净的匹配走一条简短链路并自动核销；任何含糊的都会先经验证，若仍无法有把握地归位，则连同智能体给出的最佳候选匹配一起转交你的财务团队。",
    steps: [
      "读取并规范化收款（银行流水、POS、汇款通知）",
      "分类：干净匹配、整付款、部分付款或无法识别",
      "对照实时未清发票账簿做模糊匹配",
      "在核销前验证分配能对上",
      "核销已匹配项目；其余转交人处理",
    ],
    inLabel: "一笔收款到账",
    outLabel: "发票已核销（或例外入队）",
    loopAround: {
      index: 3,
      label:
        "复核候选分配——数字是否精确到分对得上？——并在核销任何项目前重新匹配；若置信度仍低，则转交人处理而非强行匹配。",
    },
    humanStep: "你的财务团队处理智能体无法归位的例外",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "在银行流水中",
    inMessage:
      "贷记 RM 4,300.00 · 3 月 14 日 · 摘要：'SUNRISE ENT PYMT MAR'。客户 'Sunrise Enterprise' 有 3 张未清发票：INV-1021 RM 1,800、INV-1044 RM 2,000、INV-1050 RM 1,200。",
    outLabel: "智能体如何入账",
    outLines: [
      "根据摘要与既往付款习惯匹配到客户 'Sunrise Enterprise'",
      "RM 4,300 = INV-1021（RM 1,800）+ INV-1044（RM 2,000）+ RM 500 部分支付 INV-1050",
      "INV-1021 与 INV-1044 全额核销；INV-1050 部分支付，RM 700 仍未清",
      "没有留下未核销余额——全额 RM 4,300 已分配",
    ],
    outResult: "更新 3 张发票，核销 2 张 · 自动登记入账簿 · RM 0 未核销",
    note: "每笔分配都可追溯到收款与发票编号 · 人工推翻时可一步撤销 · 每笔收款 AI 成本约 RM 0.05–0.15。",
  },
  workflowDetail: [
    {
      title: "读取并规范化收款",
      detail:
        "智能体从收款到达的任何来源读取当天收款——银行流水（CAMT/MT940）、CSV、POS 结算文件或邮件汇款通知——并把每一笔规范成统一结构：金额、币种、起息日、付款方，以及随附的任何摘要文本，必要时以视觉能力读取 PDF 和图片。",
    },
    {
      title: "对收款分类",
      detail:
        "它按类型路由每一笔收款——干净的单发票匹配、一笔覆盖多张发票的整付款、部分付款，或一笔尚无法识别的——因为各自需要不同处理。这个路由步骤让常见情形走得快、疑难情形处理得细。",
    },
    {
      title: "对照未清账簿做模糊匹配",
      detail:
        "它利用客户的实时未清发票与其既往付款习惯，把收款匹配到它所支付的发票——能推断越过含糊摘要、不完全吻合的名称，或必须拆分到多张未清项目的整数款。",
    },
    {
      title: "验证并复核（循环）",
      detail:
        "在核销任何项目前，它复核分配是否精确对上——所选发票之和等于收款、币种与未清状态无误——不对则重新匹配。若仍无法有把握地对上，它会停下，而不是强行匹配。",
    },
    {
      title: "核销已匹配项目",
      detail:
        "对于有把握且已对上的匹配，它把分配登记到你的账簿并核销发票——附带幂等键，使重放的对账单无法重复入账，并作为可撤销分录，让你的团队可一步推翻。",
    },
    {
      title: "把例外交给人处理",
      detail:
        "任何无法归位的——未知付款方、金额不符、指向不明的摘要——都连同它考虑过的候选转交你的财务团队，侦探活已经做完。绝不凭猜测核销。",
    },
  ],
  howWeBuild:
    "它基于你的实时未清发票账簿工作，因此对照的是此刻真正未清的部分——已付款或已冲销的发票无法被匹配两次。当它核销一个项目时，通过带幂等键的连接工具写入一份结构化分配，因此重跑或重复的银行流水行不会把一笔收款入账两次，而每一笔分配都是可撤销的分录，你的团队可一步撤销。它绝不移动资金——它只把你已经收进银行的收款分配到它们所支付的发票，并且从影子模式起步，先提出匹配供人确认，直到它在你真实收款上的准确度赢得自动核销的资格。",
  agentLoop: [
    "收款 + 你的实时未清发票账簿与该付款方的历史",
    "分类、模糊匹配并分配（MCP 工具）",
    "在核销前复核分配精确到分对得上",
  ],
  integrations: [
    "银行流水 / 对账单导入（CAMT / MT940、CSV 或 PDF 汇款通知）",
    "POS 结算 / 支付网关打款文件",
    "ERP / 会计账簿（应收未清项目、现金入账）",
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的路由前端将每笔收款（干净 1:1 · 整付款 · 部分付款 · 无法识别）分派到简短的分类型链路；每个工具通过进程内 MCP 服务器提供。",
    "上下文：客户的实时未清发票账簿与其近期付款历史按笔载入，因此匹配对照的是此刻未清的部分，并参考该付款方的付款习惯——其摘要、取整方式以及结算到哪个实体。",
    "工具：只读的应收未清与付款历史查询、面向银行/POS/汇款格式的规范化器、确定性分配求解器（哪些未清发票之和等于本笔收款），以及一个仅向账簿登记分配的写入工具——没有任何工具能移动资金或创建、修改、删除发票。",
    "幂等与可撤销：每笔登记都带幂等键（银行流水行 ID + 金额 + 起息日），使重放的对账单或重复流水无法重复入账，而每笔分配都是可一步撤销的分录，绝非静默覆盖。",
    "防护机制与门槛：自动核销仅在超过置信度阈值、且分配精确到分对得上时才触发；部分付款、超付、超出容差的 FX 取整以及任何低于阈值的项，都连同候选匹配转入人工队列——绝不强行匹配。",
    "不可信输入与 PDPA/PDPC：对账单摘要与汇款邮件被视为不可信文本（精心构造的摘要无法左右匹配），银行信息、客户名称与金额在 MY 与 SG 实体间均按 PDPA/PDPC 处理。",
    "多币种：RM 与 SGD 收款按各自发票的币种、以入账 FX 匹配，并对银行手续费与取整设置容差，使因手续费少几分的收款也能干净核销。",
    "可观测性与审计：每次匹配决定、考虑过的候选，以及每笔登记或撤销都会留痕并可追溯到银行流水行与发票编号——一条适用于财务管控与外部审计的机器可读审计轨迹。",
    "评估框架：一组真实历史收款的黄金集——干净、整付款、部分支付、摘要错配与无法识别——配合确定性核对检查，并对模糊判断采用 LLM 作为评审，每次改动在接触实时入账前都做回归测试。",
  ],
  models: [
    {
      task: "分类并把收款模糊匹配到发票",
      model: "Claude Sonnet",
      why: "读取杂乱摘要并推断整付款与部分付款——核心判断，成本适中。",
    },
    {
      task: "高频的干净 1:1 匹配",
      model: "Claude Haiku",
      why: "大多数收款是干净的单发票匹配；处理大批量最便宜、最快。",
    },
    {
      task: "规范化多样的对账单 / 汇款格式",
      model: "Claude Haiku",
      why: "从 CSV / MT940 / PDF 行做结构化提取——一项轻量、高频的任务。",
    },
    {
      task: "处理纠缠或有争议的分配",
      model: "Claude Opus",
      why: "仅在 Sonnet 标记的最棘手整付款与争议时调用——占收款的一小部分。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每笔收款",
        value: "约 RM 0.05–0.15",
        note: "规范化 + 匹配 + 验证 + 入账，多数按 Claude Haiku/Sonnet 的 token 费率",
      },
      {
        label: "AI 用量——每月 1,500 笔收款",
        value: "约 RM 65–200 / 月",
        note: "使用 Batch API 处理非紧急的日终任务可再降低",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "接入你的银行流水、POS 打款与 ERP 入账约需数周；简短的需求梳理后我们给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比 DSO 哪怕降低几天所释放的营运资金，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于收款笔数、有多少是部分付款或整付款，以及你的摘要有多规范。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  modelChoice: {
    approach: "单一模型，按任务分级",
    rationale:
      "核销一笔收款共享同一上下文——收款及客户的未清项目——因此由一个完善配置的单一模型来做判断，用更便宜的 Haiku 处理大量干净匹配、把 Opus 仅留给纠缠的情形。可靠、每笔成本更低、也更易审计。",
    costNote:
      "我们把多智能体设计（约 15 倍 token）留给真正并行、广度优先的工作——而非一次收款到发票的核对。",
  },
  evaluation: [
    "先做确定性检查——分配是否精确等于收款、发票是否确实未清、币种是否正确",
    "自我验证——智能体在核销前复核数字并重新匹配，宁可入队也不强行匹配",
    "业务 KPI——自动匹配率、现金核销时间、未核销现金余额、DSO",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先在样本上测量你当前的现金核销——每笔收款耗时、目前有多大比例自动匹配、月末有多少挂为未核销，以及两名文员对一笔疑难收款分配的一致程度。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的，而非假设。",
    dimensions: [
      {
        dim: "Goal",
        desc: "它是否正确地完成了现金核销？",
        metrics: [
          {
            name: "分配（对比资深应收文员）",
            measures: "在留出收款上，其核销/入队决定与所选发票与专家一致",
            target: "≥ 98%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "匹配是否合理且完整？",
        metrics: [
          {
            name: "必需检查全部执行",
            measures: "每一项候选检查都执行——客户、金额、摘要、未清状态、币种——不遗漏",
            target: "100%",
          },
          {
            name: "不强行匹配",
            measures: "当候选无法与收款对上时，它宁可入队也不臆测",
            target: "持续监控",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各个步骤是否正确？",
        metrics: [
          {
            name: "分配准确率",
            measures: "它核销的发票与金额精确无误，到分",
            target: "≥ 99%",
          },
          {
            name: "自动匹配率",
            measures: "在准确度门槛下，无需人工即核销的收款比例",
            target: "≥ 80%（目标）",
          },
          {
            name: "错误核销率",
            measures: "它核销到错误发票的频率",
            target: "零容忍",
          },
          {
            name: "无未核销遗漏",
            measures: "每笔核销的收款都精确到分全额分配——没有游离余额",
            target: "100%",
          },
        ],
      },
    ],
    gate: "在智能体于历史收款黄金集（干净、整付款、部分支付与摘要错配）上超过人工基线并达到这些 GPA 目标之前，它不会向实时账簿登记。自动核销先以影子模式起步（它提议、人确认），仅当其准确度稳定后才按收款类型逐一上线。每次改动在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在你真实的历史银行对账单与未清发票账簿上做概念验证——包括整付款与摘要错配的收款——以影子模式运行，让你在它核销任何项目前先看到它的匹配。聚焦单一账户的落地只需数周。",
  ctaTitle: "你的团队还在用人工核销银行收款吗？",
  ctaDescription:
    "告诉我们你的收款笔数、开户银行与 ERP，我们会诚实告诉你现金核销智能体是否值得构建，以及可预期的自动匹配率。",
  faqs: [
    {
      question: "智能体会移动或触碰我们的资金吗？",
      answer:
        "不会。它绝不发起付款或转账。它只把你已经收进银行的收款分配到它们所支付的未清发票，并把该分配登记进你的账簿——每笔登记都可一步撤销，资金移动从不在范围内。",
    },
    {
      question: "它如何处理一笔付款覆盖多张发票，或部分付款？",
      answer:
        "这正是核心。它把整付款推断回与之相加的发票、把部分付款拆分到正确的发票并保留余额未清，而当数字无法对上时，它会连同最佳候选把收款入队，而不是强行匹配。",
    },
    {
      question: "无法识别的收款怎么办？",
      answer:
        "不会被臆测。任何低于置信门槛的——含糊摘要、金额不符、未知付款方——都连同智能体考虑过的候选转交你的财务团队：这些本来也会挂为未核销现金，只是侦探活已经做完。",
    },
    {
      question: "它能跨马来西亚和新加坡、支持多币种吗？",
      answer:
        "可以。它按各自发票的币种、以入账 FX 匹配 RM 与 SGD 收款，并对银行手续费设容差，同时处理两个实体的账簿。请将税务与审计细节视为你财务团队的起点，而非会计意见。",
    },
    {
      question: "部署需要多长时间？",
      answer:
        "针对单一银行账户或实体的概念验证通常只需数周，先以影子模式运行；全面落地需要更长时间——主要是 ERP 入账集成和让团队适应放手让它自动核销，而不是 AI 本身。",
    },
  ],
};

export const cashApplication: AgentUseCase = {
  structure: {
    slug: "cash-application",
    status: "available",
    pattern: "routing",
    route: "solutions/agent-use-cases/cash-application",
    heroImage: "/images/agent-use-cases/cash-application-hero.jpg",
    cardImage: "/images/agent-use-cases/cash-application-card.jpg",
    relatedUseCases: ["bank-reconciliation", "invoice-matching", "credit-evaluation"],
    trackLocation: "agent_use_case_cash_application",
    valueScore: 4,
    complexityScore: 4,
  },
  content: { en, ms, zh },
};
